import { ITraVosPlayEvent } from "./layerFactory/ITraVosPlayEvent";
import { DistanceCalcInteraction } from "./calc/DistanceCalcInteraction";
import { AreaCalcInteraction } from "./calc/AreaCalcInteraction";

import { LayerFactory } from "./layerFactory/LayerFactory";
import { ILayerFactory } from "./layerFactory/ILayerFactory";
import { LayerName } from "./LayerName";
import { FeatureGetInfoInteraction } from "./getInfo/FeatureGetInfoInteraction";

import { Map as OpenLayerMap } from "ol";
import { fromLonLat, transform, transformExtent } from "ol/proj";
import View from "ol/View";
import { Vector as VectorLayer, Image } from "ol/layer";
import TileLayer from "ol/layer/Tile";
import { Vector as VectorSource } from "ol/source";
import TileSource from "ol/source/Tile";
import ImageSource from "ol/source/Image";
import { defaults } from "ol/control";
import ScaleLine from "ol/control/ScaleLine";
import { MapSelector } from "./MapSelector";
import { CaptureFrame } from "./CaptureFrame";
import { SelectedFeatureInfo } from "./SelectedFeatureInfo";
import { ExportAreaSelectInteraction } from "./getInfo/ExportAreaSelectInteraction";
import { ExportArea } from "./util/ExportArea";
import { ContextMenu } from "@/components/utils/context-menu";
import { ICommonView } from "@/components/pages/common/ICommonView";
import ImageWMSEx from "./net/ImageWMSEx";
import { mapOperation } from "./MapOperation";
import { IOdDisplayEvent } from "./layerFactory/IOdDisplayEvent";

/**
 * API通信用URL
 */
const URL_API = process.env.VUE_APP_XROAD_API_URL;

/**
 * 地図クラス
 */
export class GeoMap {
  /**
   * 地図のタグ名称
   */
  private _tagName: string;

  /**
   * 共通変数:レイヤマップ
   */
  private _mapLayerList: Map<
    string,
    VectorLayer<VectorSource> | TileLayer<TileSource> | Image<ImageSource>
  > = new Map();

  /**
   * レイヤファクトリ
   */
  private _layerFactory = new LayerFactory();

  /**
   * 地図コントロール
   */
  public _map: OpenLayerMap;

  /**
   * 地図操作クラス
   */
  private _mapOperation: mapOperation;

  /**
   * 距離計測処理
   */
  private _distanceCalcInteraction: DistanceCalcInteraction;

  /**
   * 面積計測処理
   */
  private _areaCalcInteraction: AreaCalcInteraction;

  /**
   * 地物選択処理
   */
  private _featureGetInfoInteraction: FeatureGetInfoInteraction;

  /**
   * 市区町村選択処理
   */
  private _exportAreaSelectInteraction: ExportAreaSelectInteraction;

  /**
   * 市区町村選択処理 Getter
   */
  get exportAreaSelectInteraction() {
    return this._exportAreaSelectInteraction;
  }

  /**
   * 出力処理
   */
  private _exportArea: ExportArea;

  /**
   * 出力処理 Getter
   */
  get exportArea() {
    return this._exportArea;
  }

  /**
   * コンテキストメニュー
   */
  public _contextMenu: ContextMenu;

  /**
   * 画面キャプチャ用枠
   */
  private _captureFrame: CaptureFrame = null;

  /**
   * 地図表示コントロールコンストラクタ
   * @param tagName タグ名称
   */
  constructor(tagName: string) {
    this._tagName = tagName;
  }

  /**
   * 初期化
   * @param latitude 緯度
   * @param longitude 経度
   * @param zoom ズームレベル
   */
  public initMap(
    latitude: number,
    longitude: number,
    zoom: number,
    commonView: ICommonView,
    changeResolutionCallback: any,
    changeCenterCallback: any
  ) {
    if (this._map) {
      this._map.dispose();
    }
    const measureDistanceLayer = new VectorLayer({
      source: new VectorSource(),
    });
    const measureAreaLayer = new VectorLayer({
      source: new VectorSource(),
    });
    const selectedFeatureLayer = new VectorLayer({
      source: new VectorSource(),
    });
    this.addLayerMap(LayerName.MeasureDistance, measureDistanceLayer);
    this.addLayerMap(LayerName.MeasureArea, measureAreaLayer);
    this.addLayerMap(LayerName.SelectedFeature, selectedFeatureLayer);

    const selectedWmsFeature = new Image({
      source: new ImageWMSEx({
        url: URL_API,
        params: {
          LAYERS: null,
          EXCEPTIONS: "application/json",
          CQL_FILTER: null,
        },
        ratio: 1,
        serverType: "geoserver",
        crossOrigin: "Anonymous",
      }),
    });

    const selectedWmsFeatureTravos = new Image({
      source: new ImageWMSEx({
        url: URL_API,
        params: {
          LAYERS: null,
          EXCEPTIONS: "application/json",
          CQL_FILTER: null,
        },
        ratio: 1,
        serverType: "geoserver",
        crossOrigin: "Anonymous",
      }),
    });

    const wmsFeature = new Image({
      source: new ImageWMSEx({
        url: URL_API,
        params: {
          LAYERS: null,
          EXCEPTIONS: "application/json",
          CQL_FILTER: null,
        },
        ratio: 1,
        serverType: "geoserver",
        crossOrigin: "Anonymous",
      }),
    });

    const viewSorce = {
      center: fromLonLat([longitude, latitude]),
      maxZoom: 18,
      minZoom: 5,
      projection: "EPSG:3857",
      zoom: zoom,
      extent: transformExtent([0, 0, 180, 90], "EPSG:4326", "EPSG:3857"),
    };
    const lView = new View(viewSorce);

    let layers: any[] = [];
    this.getLayerFactory()
      .getLayerFactory()
      .forEach((ele: ILayerFactory, index) => {
        const layer = ele.getViewLayer();
        const FIRST_ROAD_STRUCTURES_INDEX = 6;
        if (index === FIRST_ROAD_STRUCTURES_INDEX) {
          this._exportAreaSelectInteraction = new ExportAreaSelectInteraction(
            this,
            commonView
          );
          const exportAreaLayers = this._exportAreaSelectInteraction.getLayer();
          if (exportAreaLayers) layers = layers.concat(exportAreaLayers);
        }
        if (layer) layers = layers.concat(layer);
      });
    layers.push(wmsFeature);
    layers.push(selectedWmsFeature);
    layers.push(selectedWmsFeatureTravos);
    layers.push(selectedFeatureLayer);
    layers.push(measureDistanceLayer);
    layers.push(measureAreaLayer);

    // コントロール追加
    const mapSelector = new MapSelector();
    const scaleLine = new ScaleLine();

    // 地図生成
    this._map = new OpenLayerMap({
      target: this._tagName,
      layers: layers,
      view: lView,
      controls: defaults({ zoom: true }).extend([mapSelector, scaleLine]),
      interactions: [],
    });

    this._map.getView().on("change:resolution", changeResolutionCallback);
    this._map.getView().on("change:center", changeCenterCallback);

    // 距離計測クラス初期化
    const distanceLayer = <VectorLayer<VectorSource>>(
      this._mapLayerList.get(LayerName.MeasureDistance)
    );
    this._distanceCalcInteraction = new DistanceCalcInteraction(
      this._map,
      distanceLayer.getSource()
    );

    // 面積計測クラス初期化
    const areaDistanceLayer = <VectorLayer<VectorSource>>(
      this._mapLayerList.get(LayerName.MeasureArea)
    );
    this._areaCalcInteraction = new AreaCalcInteraction(
      this._map,
      areaDistanceLayer.getSource(),
      commonView
    );

    // コンテキストメニュークラス初期化
    this._contextMenu = new ContextMenu(this._map);

    // 地物選択クラス初期化
    this._featureGetInfoInteraction = new FeatureGetInfoInteraction(
      this._map,
      selectedFeatureLayer,
      selectedWmsFeature,
      selectedWmsFeatureTravos,
      wmsFeature,
      this._contextMenu,
      this,
      commonView
    );

    // 出力クラス初期化
    this._exportArea = new ExportArea(this);

    this._mapOperation = new mapOperation(this._map);
    this._mapOperation.startDragMode();
  }

  /**
   * レイヤマップの要素追加
   * @param layerName レイヤ名
   * @param layer レイヤ
   */
  public addLayerMap(
    layerName: string,
    layer:
      | VectorLayer<VectorSource>
      | Image<ImageSource>
      | TileLayer<TileSource>
  ) {
    this._mapLayerList.set(layerName, layer);
  }

  /**
   * レイヤマップの要素削除
   * @param layerName レイヤ名
   */
  public removeLayerMap(layerName: string) {
    this._mapLayerList.delete(layerName);
  }

  /**
   * 全レイヤファクトリ取得
   */
  getLayerFactory() {
    return this._layerFactory;
  }

  /**
   * 距離計測開始
   * @param calcPointAddFunc 計測点追加コールバック
   * @param distance 距離
   */
  startCalcDistance(calcPointAddFunc: (distance: number) => void) {
    this._distanceCalcInteraction.startCalc(calcPointAddFunc);
  }

  /**
   * 距離計測終了
   */
  endCalcDistance() {
    this._distanceCalcInteraction.endCalc();
  }

  /**
   * 距離計測地点クリア
   */
  clearCalcDistance() {
    this._distanceCalcInteraction.clearCalcPoint();
  }

  /**
   * 面積計測開始
   * @param areaCalcFixFunc 面積計測確定コールバック
   * @param area 面積
   */
  startCalcArea(areaCalcFixFunc: (area: number) => void) {
    this._areaCalcInteraction.startCalc(areaCalcFixFunc);
  }

  /**
   * 面積計測終了
   */
  endCalcArea() {
    this._areaCalcInteraction.endCalc();
  }

  /**
   * 面積計測地点クリア
   */
  clearCalcArea() {
    this._areaCalcInteraction.clearCalcArea();
  }

  /**
   * 地物選択開始
   * @param selectedFeatureAttribute 地物選択コールバック
   */
  startSelectedFeature(
    selectedFeatureAttribute: (featureAttribute: SelectedFeatureInfo) => void,
    traVosPlayEvent?: ITraVosPlayEvent,
    odDisplayEvent?: IOdDisplayEvent
  ) {
    this._featureGetInfoInteraction.startSelect(
      selectedFeatureAttribute,
      traVosPlayEvent
    );
    this._layerFactory.TraVos.setTraVosPlayEvent(traVosPlayEvent);
    this._layerFactory.ODNode.setOdDisplayEvent(odDisplayEvent);
  }

  /**
   * 地物選択終了
   */
  endSelectedFeature() {
    this._featureGetInfoInteraction.endSelect();
  }

  /**
   * 地物選択状態解除
   */

  unSelectedFeature(featureId: string) {
    this._featureGetInfoInteraction.unSelectedFeature(featureId);
  }

  /**
   * 画面キャプチャ赤枠表示
   */
  showCaptureFrame(rectWidth: number, rectHeight: number) {
    if (this._captureFrame) {
      this._captureFrame.setSize(rectWidth, rectHeight);
    } else {
      this._captureFrame = new CaptureFrame(rectWidth, rectHeight);
      this._map.addControl(this._captureFrame);
    }
  }

  /**
   * 画面キャプチャ赤枠非表示
   */
  hideCaptureFrame() {
    this._map.removeControl(this._captureFrame);
    this._captureFrame = null;
  }

  /**
   * 画面状態取得
   * @returns centerLatitude 中心点緯度
   * @returns centerLongitude 中心点経度
   * @returns zoomLevel ズームレベル
   */
  getDisplayInfo() {
    const centerPosition = this._map?.getView()?.getCenter();
    if (!centerPosition) {
      return {
        centerLatitude: null,
        centerLongitude: null,
        zoomLevel: null,
      };
    }
    const center = transform(centerPosition, "EPSG:3857", "EPSG:4326");
    return {
      centerLatitude: center[1],
      centerLongitude: center[0],
      zoomLevel: this._map.getView().getZoom(),
    };
  }

  /**
   * 指定位置に移動
   * @param latitude 緯度
   * @param longitude 経度
   */
  moveMap(latitude: number, longitude: number) {
    this._map.getView().animate({
      center: fromLonLat([longitude, latitude], "EPSG:3857"),
      duration: 1000,
    });
  }
}
